"use client"

import { useForm as useMantineForm, UseFormInput, UseFormReturnType } from '@mantine/form'
import { Stack, Button, Group, Text } from '@mantine/core'
import { TbDeviceFloppy, TbX } from 'react-icons/tb'
import { ReactNode } from 'react'

export interface FormWrapperProps {
  children: ReactNode
  onSubmit: (values: any) => void | Promise<void>
  onCancel?: () => void
  submitLabel?: string
  cancelLabel?: string
  loading?: boolean
  disabled?: boolean
  submitColor?: string
  showCancel?: boolean
  spacing?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
}

/**
 * Wrapper component for forms with consistent styling and actions
 * Note: All text labels should be provided by the consuming application for i18n
 */
export function FormWrapper({
  children,
  onSubmit,
  onCancel,
  submitLabel,
  cancelLabel,
  loading = false,
  disabled = false,
  submitColor = 'forest',
  showCancel = true,
  spacing = 'md'
}: FormWrapperProps) {
  return (
    <form onSubmit={onSubmit}>
      <Stack gap={spacing}>
        {children}

        <Group justify="flex-end" gap="md" pt="md">
          {showCancel && onCancel && cancelLabel && (
            <Button
              variant="light"
              color="gray"
              leftSection={<TbX size={16} />}
              onClick={onCancel}
              disabled={loading}
            >
              {cancelLabel}
            </Button>
          )}

          {submitLabel && (
            <Button
              type="submit"
              loading={loading}
              disabled={disabled}
              leftSection={<TbDeviceFloppy size={16} />}
              color={submitColor}
            >
              {submitLabel}
            </Button>
          )}
        </Group>
      </Stack>
    </form>
  )
}

/**
 * Enhanced useForm hook with common validation patterns
 * Note: Error messages should be provided by the consuming application for i18n
 */
export function useFormWithValidation<T extends Record<string, any> = Record<string, any>>(
  input?: UseFormInput<T>
) {
  const form = useMantineForm<T>(input)

  const validateRequired = (errorMessage: string) => (value: any) => {
    if (value === null || value === undefined || value === '') {
      return errorMessage
    }
    return null
  }

  const validateEmail = (errorMessage: string) => (value: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(value) ? null : errorMessage
  }

  const validateMinLength = (min: number, errorMessage: string) => (value: string) => {
    return value.length >= min ? null : errorMessage
  }

  const validateMaxLength = (max: number, errorMessage: string) => (value: string) => {
    return value.length <= max ? null : errorMessage
  }

  const validatePattern = (pattern: RegExp, errorMessage: string) => (value: string) => {
    return pattern.test(value) ? null : errorMessage
  }

  return {
    ...form,
    validateRequired,
    validateEmail,
    validateMinLength,
    validateMaxLength,
    validatePattern
  }
}

/**
 * Form field wrapper with consistent spacing and error handling
 */
export interface FormFieldProps {
  children: ReactNode
  label?: string
  description?: string
  error?: string
  required?: boolean
  requiredIndicator?: string
  spacing?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
}

export function FormField({
  children,
  label,
  description,
  error,
  required,
  requiredIndicator = '*',
  spacing = 'sm'
}: FormFieldProps) {
  return (
    <Stack gap={spacing}>
      {label && (
        <Text size="sm" fw={500}>
          {label}
          {required && <Text component="span" c="red" ml={4}>{requiredIndicator}</Text>}
        </Text>
      )}

      {description && (
        <Text size="xs" c="dimmed">
          {description}
        </Text>
      )}

      {children}

      {error && (
        <Text size="xs" c="red">
          {error}
        </Text>
      )}
    </Stack>
  )
}
